import { AgGridReact } from '@ag-grid-community/react'
import axios from 'axios'
import { uniqBy } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { MarkupRuleWithDetail } from 'src/api'
// @ts-ignore
import Env from 'src/api/gen/Env'
import { useAccessToken } from 'src/common/hooks/auth'

const baseUrl = new URL(Env.api).host

const ROWS_PER_LOAD = 200
const MAX_CONNECTION = 8

type LoadStudyResult = {
  progress?: number
  error?: Error
  totalItems: number
  loading: boolean
  reload: () => void
  data: MarkupRuleWithDetail[]
  removeItems: (ids: string[]) => void
  addItems: (items: MarkupRuleWithDetail[]) => void
}

const doFetchMarkupRules = async (limit: number, offset: number, token: string) => {
  const result = await axios.get(`${new URL(Env.api).protocol}//${baseUrl}/api/markup-rules`, {
    params: {
      limit,
      offset,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  return result.data
}

const getMarkupRulesCount = async (token: string) => {
  const result = await axios.get(`${new URL(Env.api).protocol}//${baseUrl}/api/markup-rules/count`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return result.data || 0
}

export function useLoadMarkupRules(gridRef: React.RefObject<AgGridReact>, canFetch: boolean): LoadStudyResult {
  const [markupRuleData, setMarkupRuleData] = useState<{ data: MarkupRuleWithDetail[]; total: number }>({
    data: [],
    total: -1,
  })
  const [error, setError] = useState<Error>()
  const [availableConnection, setAvailableConnection] = useState(1)
  const [initializedData, setInitializedData] = useState(false)
  const [loadOffset, setLoadOffset] = useState(0)
  const [resetCount, setResetCount] = useState(0)
  const [accessToken] = useAccessToken()

  useEffect(() => {
    setMarkupRuleData({
      data: [],
      total: -1,
    })
    setAvailableConnection(1)
    setLoadOffset(0)
    // gridRef.current?.api?.setRowData([])
  }, [gridRef, resetCount])

  useEffect(() => {
    if (canFetch && accessToken) {
      if (markupRuleData.total === -1 || markupRuleData.data.length < markupRuleData.total) {
        if (markupRuleData.total === -1) {
          if (availableConnection > 0) {
            setAvailableConnection(0)
            getMarkupRulesCount(accessToken).then(count => {
              setMarkupRuleData(prev => ({ ...prev, total: count }))
              setAvailableConnection(MAX_CONNECTION)
              setInitializedData(true)
              // Set grid to empty if it actually has no data
              if (count === 0 && gridRef.current) {
                gridRef.current.api.setRowData([])
              }
            })
          }
        } else if (availableConnection > 0) {
          setAvailableConnection(old => old - 1)
          if (loadOffset < markupRuleData.total) {
            setLoadOffset(offset => offset + ROWS_PER_LOAD)
            doFetchMarkupRules(ROWS_PER_LOAD, loadOffset, accessToken)
              .then(items => {
                setMarkupRuleData(prev => ({ ...prev, data: uniqBy([...prev.data, ...items], 'id') }))
                setAvailableConnection(old => old + 1)
              })
              .catch(e => {
                setError(e)
              })
          }
        }
      }
    }
  }, [markupRuleData, loadOffset, availableConnection, gridRef, canFetch, accessToken])

  useEffect(() => {
    if (gridRef.current && initializedData && markupRuleData.data.length >= markupRuleData.total) {
      setInitializedData(false)
      gridRef.current.api?.setRowData(markupRuleData.data)
      gridRef.current.columnApi.autoSizeAllColumns()
      gridRef.current.api?.hideOverlay()
    }
  }, [gridRef, initializedData, markupRuleData])

  const reload = useCallback(() => {
    setResetCount(old => old + 1)
    gridRef.current?.api.showLoadingOverlay()
  }, [gridRef])

  const removeItems = useCallback(
    (ids: (string | undefined)[]) => {
      setMarkupRuleData(prev => {
        const newData = prev.data.filter(r => !ids.includes(r.id))
        return { data: newData, total: newData.length }
      })
    },
    [setMarkupRuleData],
  )

  const addItems = useCallback(
    (items: MarkupRuleWithDetail[]) => {
      if (items.length === 0) {
        return
      }
      setMarkupRuleData(prev => {
        const newMarkupRules = uniqBy([...prev.data, ...items], 'id')
        return { data: newMarkupRules, total: newMarkupRules.length }
      })
    },
    [setMarkupRuleData],
  )

  return {
    progress: Math.round(markupRuleData.total ? ((markupRuleData.data.length * 1.0) / markupRuleData.total) * 100 : 0),
    error,
    loading: markupRuleData.total === -1 || markupRuleData.data.length < markupRuleData.total,
    reload,
    totalItems: markupRuleData.data.length,
    data: markupRuleData.data,
    removeItems,
    addItems,
  }
}
